/**
 * Customer-facing copy quality helpers for product.enrich_content.
 *
 * Keep identical to `supabase/functions/_shared/product-ai-copy-quality.ts`
 * (cross-reference comment only may differ).
 *
 * These helpers polish grammar/SEO presentation from permitted facts only.
 * They must not invent benefits, reinterpret option labels, or weaken grounding.
 */

export const SEO_TITLE_MAX_LENGTH = 60;
export const META_DESCRIPTION_MAX_LENGTH = 160;
export const SHORT_DESCRIPTION_MAX_LENGTH = 200;

/** Unsupported/low-value marketing adjectives for meta and similar fields. */
export const UNSUPPORTED_MARKETING_ADJECTIVES = [
  "dependable",
  "premium",
  "high quality",
  "ideal",
  "perfect",
  "durable",
  "comfortable",
  "superior",
  "ultimate",
  "maximum",
] as const;

/**
 * Transactional CTA wording that implies purchasability / stock / listing
 * status. The enrich fact allowlist does not include those signals, so these
 * require regeneration unless explicitly present in rewrite operator copy.
 */
export const UNSUPPORTED_META_TRANSACTIONAL_CTAS = [
  "shop",
  "buy",
  "order",
  "purchase",
  "available now",
  "in stock",
] as const;

/**
 * Vague or demographic keyword terms to reject unless explicitly supplied
 * in product facts or rewrite operator copy.
 */
export const LOW_VALUE_KEYWORD_TERMS = [
  "unisex",
  "youth",
  "junior",
  "youth riding gear",
  "equestrian gear",
  "riding comfort",
  "premium leather",
] as const;

const STOPWORDS = new Set([
  "the", "a", "an", "and", "or", "of", "for", "with", "in", "on", "to", "by",
]);

export type GroundedCopyFacts = {
  name?: string | null;
  brand?: string | null;
  category?: string | null;
  options?: string[];
  values?: string[];
  optionGroups?: Record<string, string[]>;
};

export function truncateAtWordBoundary(text: string, maxLength: number): string {
  const normalized = (text ?? "").replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  if (maxLength <= 0) return "";
  if (normalized.length <= maxLength) return normalized;

  let candidate = normalized.slice(0, maxLength);
  const lastChar = candidate[candidate.length - 1] ?? "";
  const nextChar = normalized[maxLength] ?? "";
  // Only back up when the cut lands inside a word. If maxLength already falls
  // on a complete word boundary (next char is space/separator/end), keep it.
  const cutThroughWord = /[A-Za-z0-9]/.test(lastChar) && /[A-Za-z0-9]/.test(nextChar);
  if (cutThroughWord) {
    const lastSpace = candidate.lastIndexOf(" ");
    if (lastSpace > 0) candidate = candidate.slice(0, lastSpace);
  }

  candidate = candidate.trimEnd().replace(/[\s|/\-—–,:;]+$/g, "").trim();
  // Drop dangling single-letter fragments ("Bridle P").
  if (/\s[A-Za-z]$/.test(candidate)) {
    candidate = candidate.replace(/\s[A-Za-z]$/, "").trim();
    candidate = candidate.replace(/[\s|/\-—–,:;]+$/g, "").trim();
  }
  return candidate;
}

export function formatLiteralOptionList(values: string[]): string {
  const clean = values.map((v) => String(v).trim()).filter(Boolean);
  if (clean.length === 0) return "";
  if (clean.length === 1) return clean[0];
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
  return `${clean.slice(0, -1).join(", ")}, and ${clean[clean.length - 1]}`;
}

export function normalizeCustomerFacingOptionValue(value: string): string {
  const clean = String(value ?? "").replace(/\s+/g, " ").trim();
  const year = clean.match(/^(\d+)\s*year$/i);
  return year ? `${year[1]} year` : clean;
}

export function isLiteralYearSizeLabel(value: string): boolean {
  return /^\d+\s*year$/i.test(String(value).trim());
}

export function partitionLiteralOptionValues(values: string[]): {
  general: string[];
  sizeLabels: string[];
} {
  const general: string[] = [];
  const sizeLabels: string[] = [];
  for (const raw of values ?? []) {
    const value = normalizeCustomerFacingOptionValue(String(raw));
    if (!value) continue;
    if (isLiteralYearSizeLabel(value)) sizeLabels.push(value);
    else general.push(value);
  }
  return { general, sizeLabels };
}

/**
 * Natural availability phrasing that keeps option labels literal.
 * Never converts "12 year" into age suitability language.
 */
export function buildAvailabilityPhrase(
  values: string[],
  optionGroups?: Record<string, string[]>,
): string {
  const groups = Object.entries(optionGroups ?? {})
    .map(([name, rawValues]) => ({
      name: name.trim(),
      values: Array.from(new Set(
        (rawValues ?? [])
          .map((value) => normalizeCustomerFacingOptionValue(String(value)))
          .filter(Boolean),
      )),
    }))
    .filter((group) => group.name && group.values.length > 0);

  if (groups.length > 0) {
    const colour = groups.find((group) => /^colou?r$/i.test(group.name));
    const size = groups.find((group) => /^size$/i.test(group.name));
    const extras = groups.filter((group) => group !== colour && group !== size);
    const parts: string[] = [];

    if (colour) parts.push(`available in ${formatLiteralOptionList(colour.values)}`);
    if (size) parts.push(`${parts.length ? "with" : "available in"} ${formatLiteralOptionList(size.values)} size options`);
    for (const group of extras) {
      const label = group.name.toLowerCase();
      parts.push(`${parts.length ? "with" : "available in"} ${formatLiteralOptionList(group.values)} ${label} options`);
    }
    if (parts.length > 0) return parts.join(", ");
  }

  const { general, sizeLabels } = partitionLiteralOptionValues(values);
  const generalList = formatLiteralOptionList(general);
  const sizeList = formatLiteralOptionList(sizeLabels);
  if (generalList && sizeList) {
    return `available in ${generalList}, with ${sizeList} size options`;
  }
  if (generalList) return `available in ${generalList}`;
  if (sizeList) return `available in size labels ${sizeList}`;
  return "";
}

function nameAlreadyIncludesBrand(name: string, brand: string): boolean {
  if (!name || !brand) return false;
  return name.toLowerCase().includes(brand.toLowerCase());
}

/**
 * SEO title from permitted facts. Prefer product name + brand; omit low-value
 * category suffixes before truncating the product name. Never mid-word cut.
 */
export function buildGroundedSeoTitle(facts: GroundedCopyFacts): string {
  const name = (facts.name ?? "").trim() || "Product";
  const brand = (facts.brand ?? "").trim();
  const category = (facts.category ?? "").trim();

  const candidates: string[] = [];
  if (brand && !nameAlreadyIncludesBrand(name, brand)) {
    candidates.push(`${name} | ${brand}`);
    candidates.push(name);
    if (category) candidates.push(`${name} | ${brand} | ${category}`);
  } else {
    candidates.push(name);
    if (brand && nameAlreadyIncludesBrand(name, brand) && category) {
      // Brand already in name — category only if it fits without truncating name.
      candidates.push(`${name} | ${category}`);
    } else if (category) {
      candidates.push(`${name} | ${category}`);
    }
  }

  for (const candidate of candidates) {
    if (candidate.length <= SEO_TITLE_MAX_LENGTH) return candidate;
  }

  // Prefer truncating lower-value suffixes first, then the product name.
  if (brand && !nameAlreadyIncludesBrand(name, brand)) {
    const branded = truncateAtWordBoundary(`${name} | ${brand}`, SEO_TITLE_MAX_LENGTH);
    if (branded && !/\s[A-Za-z]$/.test(branded)) return branded;
  }
  return truncateAtWordBoundary(name, SEO_TITLE_MAX_LENGTH) || name.slice(0, SEO_TITLE_MAX_LENGTH);
}

function containsUnsupportedMarketingAdjective(text: string): boolean {
  const hay = text.toLowerCase();
  return UNSUPPORTED_MARKETING_ADJECTIVES.some((adj) => hay.includes(adj));
}

function looksLikeTruncatedSeoTitle(title: string): boolean {
  const t = (title ?? "").trim();
  if (!t) return true;
  if (t.length > SEO_TITLE_MAX_LENGTH) return true;
  if (/[\s|/\-—–,:;]$/.test(t)) return true;
  if (/\s[A-Za-z]$/.test(t)) return true;
  if (/\|\s*[A-Za-z]{1,2}$/.test(t)) return true;
  return false;
}

function tokenizeSeoTitle(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

/**
 * Lexical allowlist for short SEO titles: product name/brand/category/option
 * tokens, plus terms actually present in rewrite operator copy when provided.
 * Not a general semantic validator.
 */
export function buildSeoTitleTokenAllowlist(
  facts: GroundedCopyFacts,
  extraAuthorizedTerms: string[] = [],
): Set<string> {
  const allow = new Set<string>(STOPWORDS);
  const addText = (value: unknown) => {
    const text = String(value ?? "").trim();
    if (!text) return;
    for (const token of tokenizeSeoTitle(text)) allow.add(token);
  };
  addText(facts.name);
  addText(facts.brand);
  addText(facts.category);
  for (const value of facts.values ?? []) addText(value);
  for (const option of facts.options ?? []) addText(option);
  for (const extra of extraAuthorizedTerms) addText(extra);
  return allow;
}

/** True when SEO title contains a token not present in the grounded allowlist. */
export function seoTitleHasUngroundedTokens(
  title: string,
  allowlist: Set<string>,
): boolean {
  for (const token of tokenizeSeoTitle(title)) {
    if (STOPWORDS.has(token)) continue;
    if (!allowlist.has(token)) return true;
  }
  return false;
}

function metaContainsExactPhrase(text: string, phrase: string): boolean {
  const needle = phrase.toLowerCase().trim();
  if (!needle) return false;
  if (needle.includes(" ")) return text.toLowerCase().includes(needle);
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`\\b${escaped}\\b`, "i").test(text);
}

/** Unauthorized transactional CTAs present in meta (lexical phrase check only). */
export function findUnauthorizedMetaTransactionalCtas(
  meta: string,
  authorizedCorpus = "",
): string[] {
  const hits: string[] = [];
  for (const phrase of UNSUPPORTED_META_TRANSACTIONAL_CTAS) {
    if (!metaContainsExactPhrase(meta, phrase)) continue;
    if (metaContainsExactPhrase(authorizedCorpus, phrase)) continue;
    hits.push(phrase);
  }
  return hits;
}

function looksLikeWeakMeta(meta: string, authorizedCorpus = ""): boolean {
  const t = (meta ?? "").trim();
  if (!t) return true;
  if (containsUnsupportedMarketingAdjective(t)) return true;
  if (findUnauthorizedMetaTransactionalCtas(t, authorizedCorpus).length > 0) return true;
  if (/\bfalls under\b/i.test(t)) return true;
  if (/\bbelongs to\b/i.test(t)) return true;
  if (/\bproduct category is\b/i.test(t)) return true;
  if (t.length > META_DESCRIPTION_MAX_LENGTH) return true;
  if (/\s[A-Za-z]$/.test(t) && t.length >= META_DESCRIPTION_MAX_LENGTH - 1) return true;
  return false;
}

/**
 * Neutral factual meta from permitted facts. Avoids "Shop" (no listing-status
 * signal in the enrich fact allowlist) and unsupported marketing adjectives.
 */
export function buildGroundedMetaDescription(facts: GroundedCopyFacts): string {
  const name = (facts.name ?? "").trim() || "Product";
  const brand = (facts.brand ?? "").trim();
  const availability = buildAvailabilityPhrase(facts.values ?? [], facts.optionGroups);

  let sentence: string;
  if (availability) {
    const subject = nameAlreadyIncludesBrand(name, brand) || !brand
      ? `The ${name}`
      : `The ${name} by ${brand}`;
    sentence = `${subject} is ${availability}.`;
  } else if (brand && !nameAlreadyIncludesBrand(name, brand)) {
    sentence = `${name} by ${brand}.`;
  } else {
    sentence = `${name}.`;
  }

  const normalizedSentence = sentence.replace(/\s+/g, " ").trim();
  if (normalizedSentence.length > META_DESCRIPTION_MAX_LENGTH && facts.optionGroups) {
    const groups = Object.entries(facts.optionGroups);
    const colourValues = groups.find(([group]) => /^colou?r$/i.test(group.trim()))?.[1] ?? [];
    const sizeValues = groups.find(([group]) => /^size$/i.test(group.trim()))?.[1] ?? [];
    if (colourValues.length > 0 && sizeValues.length > 0) {
      const colourList = formatLiteralOptionList(
        colourValues.map((value) => normalizeCustomerFacingOptionValue(String(value))),
      );
      const sizeList = formatLiteralOptionList(
        sizeValues.map((value) => normalizeCustomerFacingOptionValue(String(value))),
      );
      const subject = brand && !nameAlreadyIncludesBrand(name, brand) ? `${name} by ${brand}` : name;
      const compact = `${subject}: ${colourList}; sizes ${sizeList}.`;
      if (compact.length <= META_DESCRIPTION_MAX_LENGTH) return compact;
    }
  }

  return truncateAtWordBoundary(normalizedSentence, META_DESCRIPTION_MAX_LENGTH);
}

/**
 * Concise customer-facing description from sparse facts.
 * No internal/database category phrasing; literal option labels only.
 */
export function buildGroundedDescription(facts: GroundedCopyFacts): string {
  const name = (facts.name ?? "").trim() || "Product";
  const brand = (facts.brand ?? "").trim();
  const category = (facts.category ?? "").trim();
  const availability = buildAvailabilityPhrase(facts.values ?? [], facts.optionGroups);

  const subject = nameAlreadyIncludesBrand(name, brand) || !brand
    ? name
    : `${brand} ${name}`;

  // Prefer natural subject + availability. Do not use internal phrasing such as
  // "falls under the X category". Mention category only when it is not already
  // present in the product name and there is no richer availability phrase.
  let description: string;
  if (availability) {
    description = `${subject} ${availability}.`;
  } else if (
    category
    && !name.toLowerCase().includes(category.toLowerCase())
    && !name.toLowerCase().includes(category.toLowerCase().replace(/s$/, ""))
  ) {
    description = `${subject}, ${category}.`;
  } else {
    description = `${subject}.`;
  }

  return description.replace(/\s+/g, " ").trim().replace(/\s+\./g, ".").replace(/\.\.+/g, ".");
}

function addKeywordTerm(terms: string[], seen: Set<string>, raw: string): void {
  const term = raw.replace(/\s+/g, " ").trim();
  if (!term) return;
  const key = term.toLowerCase();
  if (seen.has(key)) return;
  if (STOPWORDS.has(key)) return;
  seen.add(key);
  // Canonical keyword form is lowercase for stable dedupe/display.
  terms.push(key);
}

function factKeywordAllowlist(facts: GroundedCopyFacts): Set<string> {
  const allow = new Set<string>();
  for (const value of [
    facts.name,
    facts.brand,
    facts.category,
    ...(facts.values ?? []),
  ]) {
    const raw = String(value ?? "").trim();
    const t = raw.toLowerCase();
    if (t) allow.add(t);
    const normalized = normalizeCustomerFacingOptionValue(raw).toLowerCase();
    if (normalized) allow.add(normalized);
  }
  return allow;
}

/** Grounded keyword/tag candidates from permitted facts (priority: identity, options, name phrases). */
export function collectGroundedKeywordCandidates(
  facts: GroundedCopyFacts,
  extraAuthorizedTerms: string[] = [],
): string[] {
  const terms: string[] = [];
  const seen = new Set<string>();
  const name = (facts.name ?? "").trim();
  const brand = (facts.brand ?? "").trim();
  const category = (facts.category ?? "").trim();

  if (brand) addKeywordTerm(terms, seen, brand);
  if (category) addKeywordTerm(terms, seen, category);
  for (const value of facts.values ?? []) {
    addKeywordTerm(terms, seen, normalizeCustomerFacingOptionValue(String(value)));
  }

  if (name) {
    // Keep the complete product name as the identity phrase. Do not explode it
    // into adjacent bigrams or isolated words: those create low-value fragments
    // such as "leather foreway", "foreway elastic", or "half".
    addKeywordTerm(terms, seen, name);
  }

  for (const extra of extraAuthorizedTerms) {
    // Extra terms may be long prose; only keep short phrase-like tokens.
    const chunk = String(extra ?? "").replace(/\s+/g, " ").trim();
    if (!chunk || chunk.length > 48 || chunk.includes(".")) continue;
    addKeywordTerm(terms, seen, chunk);
  }

  return terms.slice(0, 24);
}

function isLowValueKeyword(term: string): boolean {
  const key = term.toLowerCase().trim();
  if (!key) return true;
  for (const blocked of LOW_VALUE_KEYWORD_TERMS) {
    if (key === blocked || key.includes(blocked)) return true;
  }
  // Mechanical "N year size" phrasing.
  if (/^\d+\s*year\s+size$/i.test(key)) return true;
  return false;
}

/**
 * Keep grounded keywords/tags; drop duplicates, low-value demographics
 * (unless present in product facts), and awkward mechanical size phrases.
 * Poor legacy SEO terms in rewrite copy do not authorize Unisex/Youth guesses.
 */
export function normalizeGroundedKeywords(
  candidates: string[],
  facts: GroundedCopyFacts,
  extraAuthorizedTerms: string[] = [],
): string[] {
  const factAllow = factKeywordAllowlist(facts);
  const allowlist = new Set<string>([
    ...factAllow,
    ...collectGroundedKeywordCandidates(facts, extraAuthorizedTerms),
  ]);

  const priorityDefaults = [
    (facts.brand ?? "").trim(),
    (facts.category ?? "").trim(),
    ...(facts.values ?? []).map((v) => normalizeCustomerFacingOptionValue(String(v))),
  ].filter(Boolean);

  const groundedDefaults = collectGroundedKeywordCandidates(facts, []);
  const incoming = candidates
    .flatMap((c) => String(c).split(","))
    .map((c) => c.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  const out: string[] = [];
  const seen = new Set<string>();

  const consider = (term: string) => {
    const key = term.toLowerCase().replace(/\s+/g, " ").trim();
    if (!key || seen.has(key)) return;
    if (isLowValueKeyword(key) && !factAllow.has(key)) return;
    // Accept terms that are grounded in facts/name phrases (not long prose blobs).
    const grounded = allowlist.has(key)
      || [...factAllow].some((a) => a === key || a.includes(key));
    if (!grounded) return;
    seen.add(key);
    out.push(key);
  };

  for (const term of priorityDefaults) consider(term);
  for (const term of incoming) consider(term);
  for (const term of groundedDefaults) consider(term);

  return out.slice(0, 12);
}

/**
 * SEO titles are short structured fields — use conservative lexical grounding
 * (plus truncation/dangling checks). Unsupported marketing descriptors that
 * are not present in facts (or rewrite operator copy terms) force regeneration.
 */
export function seoTitleNeedsRegeneration(
  title: string,
  facts: GroundedCopyFacts = {},
  extraAuthorizedTerms: string[] = [],
): boolean {
  if (looksLikeTruncatedSeoTitle(title)) return true;
  const allowlist = buildSeoTitleTokenAllowlist(facts, extraAuthorizedTerms);
  if (seoTitleHasUngroundedTokens(title, allowlist)) return true;
  // Multi-word marketing phrases (e.g. "high quality") also force regeneration
  // unless every token is already fact-authorized.
  const hay = title.toLowerCase();
  for (const adj of UNSUPPORTED_MARKETING_ADJECTIVES) {
    if (!hay.includes(adj)) continue;
    const adjTokens = tokenizeSeoTitle(adj);
    if (adjTokens.some((t) => !allowlist.has(t))) return true;
  }
  return false;
}

/**
 * Meta regeneration gate. Rejects unsupported adjectives, internal phrasing,
 * truncation defects, and unauthorized transactional CTAs (Shop/Buy/…).
 * Rewrite operator copy may authorize an exact CTA phrase when present; facts
 * alone never imply purchasability.
 */
export function metaDescriptionNeedsRegeneration(
  meta: string,
  _facts: GroundedCopyFacts = {},
  extraAuthorizedTerms: string[] = [],
): boolean {
  const authorizedCorpus = extraAuthorizedTerms.join("\n");
  return looksLikeWeakMeta(meta, authorizedCorpus);
}

/**
 * Polish SEO/title/keywords for a draft using only grounded facts.
 * Does not rewrite body copy claims (operator/AI description stays unless empty).
 */
export function applyGroundedCopyQuality(
  draft: {
    description: string;
    short_description: string;
    seo_title: string;
    meta_description: string;
    tags: string[];
    seo_keywords: string;
  },
  facts: GroundedCopyFacts,
  extraAuthorizedTerms: string[] = [],
): {
  description: string;
  short_description: string;
  seo_title: string;
  meta_description: string;
  tags: string[];
  seo_keywords: string;
} {
  const description = (draft.description ?? "").trim();
  let short_description = (draft.short_description ?? "").trim();
  if (short_description.length > SHORT_DESCRIPTION_MAX_LENGTH) {
    short_description = truncateAtWordBoundary(short_description, SHORT_DESCRIPTION_MAX_LENGTH);
  }

  let seo_title = (draft.seo_title ?? "").trim();
  if (seoTitleNeedsRegeneration(seo_title, facts, extraAuthorizedTerms)) {
    seo_title = buildGroundedSeoTitle(facts);
  } else {
    seo_title = truncateAtWordBoundary(seo_title, SEO_TITLE_MAX_LENGTH);
    if (seoTitleNeedsRegeneration(seo_title, facts, extraAuthorizedTerms)) {
      seo_title = buildGroundedSeoTitle(facts);
    }
  }

  let meta_description = (draft.meta_description ?? "").trim();
  if (metaDescriptionNeedsRegeneration(meta_description, facts, extraAuthorizedTerms)) {
    meta_description = buildGroundedMetaDescription(facts);
  } else {
    meta_description = truncateAtWordBoundary(meta_description, META_DESCRIPTION_MAX_LENGTH);
    if (metaDescriptionNeedsRegeneration(meta_description, facts, extraAuthorizedTerms)) {
      meta_description = buildGroundedMetaDescription(facts);
    }
  }

  const keywordSource = [
    ...draft.tags,
    ...String(draft.seo_keywords ?? "").split(","),
  ];
  const tags = normalizeGroundedKeywords(keywordSource, facts, extraAuthorizedTerms);
  const seo_keywords = tags.join(", ");

  return {
    description,
    short_description: short_description || truncateAtWordBoundary(description, SHORT_DESCRIPTION_MAX_LENGTH),
    seo_title,
    meta_description,
    tags,
    seo_keywords,
  };
}
